import { useContext, useState, useEffect } from "react";
import { useAppStateContext } from "../../state/AppContextProvider";
import { useNavigate } from "react-router-dom";
import { useConfig } from "../../config/ConfigProvider";
import productImgDark from "../../assets/product4_dark.png";
import productImgLight from "../../assets/product4_light.png";
import { getUserPhoto } from "../../auth/getAccessToken";
import { AccessContext } from "../../state/AccessProvider";
import { Box, Button, useTheme, Card, CardContent, Typography, Switch, FormControlLabel } from "@mui/material";
import { HttpServiceContext } from "../../state/HttpProvider";
import { useOAuth } from "../../auth/OAuthProvider";

// Subscription plan types
type SubscriptionPlan = {
  id: string;
  name: string;
  priceId: string;
  productId: string;
  price: number;
  billingInterval: 'monthly' | 'yearly';
  tier: 'personal' | 'organizational-sharing' | 'organizational-advanced';
  description: string;
  features: string[];
  recommended?: boolean;
};

type BillingInterval = 'monthly' | 'yearly';

const PRODUCT_ID = 'prod_THMVFtg5m2VZUG';

const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  // Monthly Plans
  {
    id: 'personal-monthly',
    name: 'Personal Productivity',
    priceId: 'price_1SKnme3JJrvQVDu560DMJ9wK',
    productId: PRODUCT_ID,
    price: 25,
    billingInterval: 'monthly',
    tier: 'personal',
    description: 'Perfect for individual users',
    features: [
      'AI-powered negotiation insights',
      'Real-time conversation guidance',
      'Export conversations',
      'Advanced analytics',
      'Priority support'
    ]
  },
  {
    id: 'organizational-sharing-monthly',
    name: 'Organizational (Sharing)',
    priceId: 'price_1SmLaP3JJrvQVDu5ht01H86I',
    productId: PRODUCT_ID,
    price: 50,
    billingInterval: 'monthly',
    tier: 'organizational-sharing',
    description: 'Team collaboration with shared resources',
    features: [
      'Everything in Personal',
      'Team sharing & collaboration',
      'Shared conversation history',
      'Team analytics dashboard',
      'Multi-user access',
      '5 team members included'
    ],
    recommended: true
  },
  {
    id: 'organizational-advanced-monthly',
    name: 'Organizational (Advanced + Sharing)',
    priceId: 'price_1SmLav3JJrvQVDu5ZhxrN8dD',
    productId: PRODUCT_ID,
    price: 100,
    billingInterval: 'monthly',
    tier: 'organizational-advanced',
    description: 'Full enterprise features + team collaboration',
    features: [
      'Everything in Organizational (Sharing)',
      'Advanced AI models',
      'Custom integrations',
      'SSO authentication',
      'Dedicated account manager',
      'SLA guarantee',
      'Unlimited team members'
    ]
  },
  // Yearly Plans
  {
    id: 'personal-yearly',
    name: 'Personal Productivity',
    priceId: 'price_1Sma9b3JJrvQVDu5WjBOUZuH',
    productId: PRODUCT_ID,
    price: 250,
    billingInterval: 'yearly',
    tier: 'personal',
    description: 'Perfect for individual users',
    features: [
      'AI-powered negotiation insights',
      'Real-time conversation guidance',
      'Export conversations',
      'Advanced analytics',
      'Priority support',
      '💰 2 months free'
    ]
  },
  {
    id: 'organizational-sharing-yearly',
    name: 'Organizational (Sharing)',
    priceId: 'price_1SmaAC3JJrvQVDu5f5kurHPH',
    productId: PRODUCT_ID,
    price: 500,
    billingInterval: 'yearly',
    tier: 'organizational-sharing',
    description: 'Team collaboration with shared resources',
    features: [
      'Everything in Personal',
      'Team sharing & collaboration',
      'Shared conversation history',
      'Team analytics dashboard',
      'Multi-user access',
      '5 team members included',
      '💰 2 months free'
    ],
    recommended: true
  },
  {
    id: 'organizational-advanced-yearly',
    name: 'Organizational (Advanced + Sharing)',
    priceId: 'price_1SmaBE3JJrvQVDu5GkuQb0LQ',
    productId: PRODUCT_ID,
    price: 1000,
    billingInterval: 'yearly',
    tier: 'organizational-advanced',
    description: 'Full enterprise features + team collaboration',
    features: [
      'Everything in Organizational (Sharing)',
      'Advanced AI models',
      'Custom integrations',
      'SSO authentication',
      'Dedicated account manager',
      'SLA guarantee',
      'Unlimited team members',
      '💰 2 months free'
    ]
  }
];

interface SubscriptionFormProps {
  onSuccess: () => void;
  selectedPlan: SubscriptionPlan;
  setSelectedPlan: (plan: SubscriptionPlan) => void;
  billingInterval: BillingInterval;
  setBillingInterval: (interval: BillingInterval) => void;
}

const SubscriptionForm: React.FC<SubscriptionFormProps> = ({ 
  onSuccess, 
  selectedPlan, 
  setSelectedPlan,
  billingInterval,
  setBillingInterval 
}) => {
  const { httpService } = useContext(HttpServiceContext);
  const accessProvider = useContext(AccessContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubscribe = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔍 Selected Plan:', selectedPlan);
      console.log('🔍 Sending to backend:', {
        priceId: selectedPlan.priceId,
        productId: selectedPlan.productId
      });

      const response = await httpService.createCheckoutSession({
        email: accessProvider.decodedToken?.preferred_username || "",
        name: accessProvider.decodedToken?.name || accessProvider.decodedToken?.displayName || "",
        successUrl: `${window.location.origin}/#/subscription-success`,
        cancelUrl: `${window.location.origin}/#/subscription-cancel`,
        priceId: selectedPlan.priceId,
        productId: selectedPlan.productId
      });

      // Redirect to Stripe Checkout
      window.location.href = response.checkoutUrl;
    } catch (err: any) {
      setError(err.message || 'Failed to create checkout session');
      setLoading(false);
    }
  };

  // Filter plans by current billing interval
  const availablePlans = SUBSCRIPTION_PLANS.filter(p => p.billingInterval === billingInterval);

  return (
    <div style={{ marginTop: 12, width: "100%", textAlign: "center" }}>
      {/* Billing interval toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 3 }}>
        <Typography sx={{ marginRight: 2, fontWeight: billingInterval === 'monthly' ? 600 : 400, color: billingInterval === 'monthly' ? 'text.primary' : 'text.secondary' }}>
          Monthly
        </Typography>
        <Switch
          checked={billingInterval === 'yearly'}
          onChange={(e) => {
            const newInterval = e.target.checked ? 'yearly' : 'monthly';
            setBillingInterval(newInterval);
            // Switch to corresponding plan in the new interval
            const currentTier = selectedPlan.tier;
            const newPlan = SUBSCRIPTION_PLANS.find(p => p.tier === currentTier && p.billingInterval === newInterval);
            if (newPlan) {
              setSelectedPlan(newPlan);
            }
          }}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: '#e50c20',
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: '#e50c20',
            },
          }}
        />
        <Typography sx={{ marginLeft: 2, fontWeight: billingInterval === 'yearly' ? 600 : 400, color: billingInterval === 'yearly' ? 'text.primary' : 'text.secondary' }}>
          Yearly <Box component="span" sx={{ color: '#e50c20', fontWeight: 600 }}>(2 months free)</Box>
        </Typography>
      </Box>

      {/* Plan cards */}
      <Box sx={{ display: 'flex', gap: 3, marginBottom: 3, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'flex-start' }}>
        {availablePlans.map((plan) => (
          <Card
            key={plan.id}
            onClick={() => setSelectedPlan(plan)}
            sx={{
              flex: '1 1 280px',
              maxWidth: 320,
              height: 450,
              cursor: 'pointer',
              border: selectedPlan.id === plan.id ? '3px solid #e50c20' : '2px solid #e0e0e0',
              borderRadius: '16px',
              transition: 'all 0.2s ease',
              paddingTop: '32px',
              backgroundColor: selectedPlan.id === plan.id ? 'rgba(229, 12, 32, 0.05)' : 'background.paper',
              position: 'relative',
              '&:hover': {
                borderColor: '#e50c20',
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(229, 12, 32, 0.15)',
              }
            }}
          >
            {plan.recommended && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: '#e50c20',
                  color: 'white',
                  padding: '4px 16px',
                  marginTop: '10px',
                  borderRadius: '12px',
                  fontSize: 12,
                  fontWeight: 600,
                  boxShadow: '0 2px 8px rgba(229, 12, 32, 0.3)'
                }}
              >
                MOST POPULAR
              </Box>
            )}
            <CardContent sx={{ padding: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, marginBottom: 1, color: 'text.primary', minHeight: 56 }}>
                {plan.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', marginBottom: 2, minHeight: 40 }}>
                {plan.description}
              </Typography>
              <Box sx={{ marginBottom: 3, minHeight: 48 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#e50c20', display: 'inline' }}>
                  ${plan.price}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'inline' }}>
                  /{billingInterval === 'monthly' ? 'mo' : 'yr'}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'left' }}>
                {plan.features.map((feature, idx) => (
                  <Typography key={idx} variant="body2" sx={{ color: 'text.secondary', marginBottom: 0.5, fontSize: 13 }}>
                    {feature.startsWith('💰') ? feature : `✓ ${feature}`}
                  </Typography>
                ))}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box sx={{ 
        marginBottom: 2, 
        padding: 2, 
        backgroundColor: "rgba(0, 0, 0, 0.05)",
        borderRadius: "8px",
        fontSize: 14,
        color: "text.secondary",
        maxWidth: 1000,
        marginLeft: 'auto',
        marginRight: 'auto',
        textAlign: 'center'
      }}>
        You'll be redirected to Stripe's secure checkout to complete your subscription.
      </Box>
      
      {error && (
        <div style={{ 
          color: "#d32f2f", 
          marginBottom: 16, 
          fontSize: "14px",
          textAlign: "center"
        }}>
          {error}
        </div>
      )}

      <Button
        onClick={handleSubscribe}
        disabled={loading}
        variant="contained"
        sx={{
          width: "100%",
          maxWidth: 400,
          marginLeft: 'auto',
          marginRight: 'auto',
          display: 'block',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: "white",
          padding: "14px 32px",
          fontSize: 16,
          fontWeight: 600,
          borderRadius: "8px",
          textTransform: "none",
          boxShadow: "0 4px 16px rgba(102, 126, 234, 0.3)",
          "&:hover": {
            background: 'linear-gradient(135deg, #5568d3 0%, #6a4291 100%)',
            boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
          },
          "&:disabled": {
            background: "#ccc",
            boxShadow: "none",
          }
        }}
      >
        {loading ? 'Processing...' : `Subscribe to ${selectedPlan.name}`}
      </Button>
    </div>
  );
};

const RedirectLandingPageV2: React.FC = () => {
  const {
    isApproved,
    refreshApprovalStatus,
    subscriptionVerified,
    refreshSubscriptionStatus,
  } = useAppStateContext();
  const config = useConfig();
  const { refreshUser } = useOAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [showPending, setShowPending] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly');
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan>(SUBSCRIPTION_PLANS[1]); // Default to recommended plan
  const accessProvider = useContext(AccessContext);
  const productImg = theme.palette.mode === 'dark' ? productImgDark : productImgLight;

  // Logout handler
  const handleLogout = async () => {
    await accessProvider.handleLogout();
  };

  // Redirect if approved
  useEffect(() => {
    if (isApproved) {
      navigate("/");
    }
  }, [isApproved, navigate]);

  // Show pending after 5 seconds if not approved
  useEffect(() => {
    if (isApproved !== true) {
      const timer = setTimeout(() => {
        setShowPending(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
    setShowPending(false);
    return undefined;
  }, [isApproved]);

  useEffect(() => {
    if (showPending) {
      localStorage.setItem("userSignedUp", "true");
    }
  }, [showPending]);

  // Show subscription after 1 second (just for aesthetics)
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSubscription(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Fetch profile photo
  useEffect(() => {
    const fetchProfilePhoto = async () => {
      try {
        if (accessProvider.accessToken) {
          const photo = await getUserPhoto(accessProvider.accessToken);
          setProfilePhoto(photo);
        }
      } catch (error) {
        console.error("Error fetching profile photo:", error);
      }
    };
    fetchProfilePhoto();
  }, [accessProvider.accessToken]);

  const handleCheckAccess = async () => {
    try {
      await refreshApprovalStatus();
      await refreshSubscriptionStatus();
      
      if (isApproved) {
        navigate("/");
      }
    } catch (error) {
      console.error("Error checking access:", error);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        padding: "16px",
        bgcolor: "background.default",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 1200,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 2,
        }}
      >
        <Box sx={{ fontSize: 24, fontWeight: 700, color: 'text.primary' }}>
          Negotiator Power Up
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {profilePhoto && (
            <img
              src={profilePhoto}
              alt="Profile"
              style={{ width: 40, height: 40, borderRadius: "50%" }}
            />
          )}
          {showPending && !isApproved && (
            <Button
              onClick={handleCheckAccess}
              variant="outlined"
              sx={{
                textTransform: "none",
                borderColor: "text.secondary",
                color: "text.primary",
                "&:hover": {
                  borderColor: "text.primary",
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                },
              }}
            >
              Check Access
            </Button>
          )}
          <Button
            onClick={handleLogout}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderColor: "#e50c20",
              color: "#e50c20",
              "&:hover": {
                borderColor: "#c00a1a",
                backgroundColor: "rgba(229, 12, 32, 0.05)",
              },
            }}
          >
            Logout
          </Button>
        </Box>
      </Box>

      {/* Main content - Full width subscription form */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 1200,
          position: "relative",
        }}
      >
        {/* Overlay */}
        {showOverlay && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(60,60,60,0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              borderRadius: 16,
            }}
          >
            <Box
              component="span"
              sx={{
                color: "text.secondary",
                fontSize: 24,
                fontWeight: 700,
                textAlign: "center",
                letterSpacing: "0.03em",
                lineHeight: 1.4,
                textShadow: "0 2px 12px rgba(0,0,0,0.18)",
                padding: "0 16px",
              }}
            >
              Subscriptions are part of beta release. Please check back later.
            </Box>
          </div>
        )}

        {/* Hero section */}
        <Box sx={{ textAlign: 'center', marginBottom: 3 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, marginBottom: 1, color: 'text.primary' }}>
            Choose Your Plan
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', marginBottom: 2 }}>
            Maximize your negotiation potential with AI-powered insights
          </Typography>
          <img src={productImg} alt="Product" style={{ display: 'none' }} />
        </Box>

        {showSubscription && (
          <SubscriptionForm
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
            billingInterval={billingInterval}
            setBillingInterval={setBillingInterval}
            onSuccess={async () => {
              await refreshApprovalStatus();
            }}
          />
        )}

        {/* Footer */}
        <Box sx={{ marginTop: 3, textAlign: 'center', fontSize: 12, color: "#888" }}>
          Powered by <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>stripe</Box> &nbsp; | &nbsp; 
          <a href="#" style={{ color: '#888', textDecoration: 'none' }}>Terms</a> &nbsp; 
          <a href="#" style={{ color: '#888', textDecoration: 'none' }}>Privacy</a>
        </Box>
      </Box>
    </Box>
  );
};

export default RedirectLandingPageV2;
